import { isReactNativeWebView, hasReactNativeMessageChannel } from 'src/utils/webview-detection';
import { AuthUtils } from './Auth.utils';
import { AuthJwtObject } from './Auth.types';

/**
 * Service to handle authentication in a React Native WebView context
 */
export class WebViewAuthService {
  /**
   * Checks if we're running in a WebView and can receive auth tokens
   */
  static isWebViewAuthEnabled(): boolean {
    return isReactNativeWebView() && hasReactNativeMessageChannel();
  }

  /**
   * Custom event name for secure WebView authentication
   * This specific name creates a dedicated channel for auth messages only
   */
  private static readonly AUTH_EVENT_NAME = 'BECREATIVES_AUTH_EVENT';

  /**
   * Listens for authentication messages from the React Native app
   * @param callback Function to call when auth data is received
   */
  static listenForAuthMessages(callback: (token: string) => void): () => void {
    if (!this.isWebViewAuthEnabled()) {
      return () => {}; // Return no-op cleanup if not in WebView
    }

    // Handler for receiving secure auth events
    const handleAuthEvent = (event: CustomEvent) => {
      try {
        const data = event.detail;

        // Validate the auth data format and required properties
        if (
          data &&
          typeof data === 'object' &&
          'token' in data &&
          typeof data.token === 'string' &&
          data.token.length > 0
        ) {
          callback(data.token);
          window.location.replace(data.route + window.location.search);
        } else {
          console.warn('Received invalid auth data format');
        }
      } catch (error) {
        console.error('Error processing WebView auth event:', error);
      }
    };

    // Set up a bridge function to handle regular postMessage from React Native
    // and convert it to our custom event
    const messageEventBridge = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);

        // Only process auth token messages
        if (data && data.type === 'AUTH_TOKEN' && data.token) {
          // Dispatch our secure custom event
          window.dispatchEvent(
            new CustomEvent(this.AUTH_EVENT_NAME, {
              detail: { token: data.token, route: data.route },
            }),
          );
        }
      } catch (error) {
        // Silently ignore parsing errors - could be unrelated messages
      }
    };

    // Add event listeners
    window.addEventListener(this.AUTH_EVENT_NAME, handleAuthEvent as EventListener);
    window.addEventListener('message', messageEventBridge);

    // Return cleanup function
    return () => {
      window.removeEventListener(this.AUTH_EVENT_NAME, handleAuthEvent as EventListener);
      window.removeEventListener('message', messageEventBridge);
    };
  }

  /**
   * Sends a message to the React Native app to request authentication
   * Uses a secure format with a nonce to prevent replay attacks
   */
  static requestAuthFromNative(): void {
    if (!this.isWebViewAuthEnabled() || !('ReactNativeWebView' in window)) {
      return;
    }

    // Generate a random nonce for this request
    // This helps prevent replay attacks and makes each request unique
    const nonce =
      Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

    // Create a timestamp for the request
    const timestamp = Date.now();

    // We've already checked that ReactNativeWebView exists
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({
        type: 'REQUEST_AUTH_TOKEN',
        secure: true,
        eventName: this.AUTH_EVENT_NAME,
        nonce,
        timestamp,
      }),
    );

    console.log(`Sent secure auth request with event: ${this.AUTH_EVENT_NAME}`);
  }

  /**
   * Sets authentication data received from the WebView
   * @param token JWT token received from React Native
   */
  static setWebViewAuth(token: string): AuthJwtObject {
    // Create JWT object from token
    const authObj = AuthUtils.make(token);

    // Save to local storage
    AuthUtils.setJwt(authObj);

    return authObj;
  }
}
